/**
 * ProjectWidget - Form laterale per creare/modificare progetti
 *
 * Segue il pattern DetailPanel per aprire lateralmente su desktop
 * e fullscreen su mobile.
 *
 * Step 1: Metodo di creazione (da zero, template, AI wizard)
 * Step 2: Dettagli progetto (nome, azienda, workflow, descrizione)
 * Step 3: Budget e team
 */

import { useState, useMemo, useEffect } from 'react';
import {
  X,
  Plus,
  Briefcase,
  Check,
  Sparkles,
  FileText,
  Layers,
  Search,
  ChevronDown,
} from 'lucide-react';
import { useProjectsStore, useCompanies, useCreateCompany, type Company } from '@business-assistant/shared';
import { useTeamMembers } from '@business-assistant/shared';
import type { ProjectStatus } from '@business-assistant/shared/domain';
import { DetailPanel, DetailSection } from '../common/base';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../common/forms';

// =============================================================================
// TYPES
// =============================================================================

interface ProjectFormProps {
  projectId?: string;
  onClose: () => void;
  onSave: (data: ProjectFormData) => void;
}

export interface ProjectFormData {
  title: string;
  description: string;
  clientName: string;
  clientId?: string;
  status: ProjectStatus;
  startAt: string;
  dueAt: string;
  budget?: number;
  budgetType: 'no-budget' | 'fixed-fee' | 'hourly' | 'retainer';
  contractId?: string;
  workflow?: string;
  team: string[];
  ownerId?: string;
  tags: string[];
  creationMethod: 'scratch' | 'template' | 'ai-wizard';
}

type CreationMethod = 'scratch' | 'template' | 'ai-wizard';
type BudgetType = 'no-budget' | 'fixed-fee' | 'hourly' | 'retainer';

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  sectionTitle: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--text-muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    marginBottom: '0.75rem',
    marginTop: '1.5rem',
  },
  formGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    fontWeight: 500,
    marginBottom: '0.375rem',
    fontSize: '0.875rem',
  },
  input: {
    width: '100%',
    padding: '0.625rem',
    border: '1px solid var(--line)',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
  },
  helpText: {
    fontSize: '0.75rem',
    color: 'var(--text-muted)',
    marginTop: '0.25rem',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
  },
  radioOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem',
    border: '1px solid var(--line)',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  radioOptionSelected: {
    border: '2px solid var(--brand)',
    background: 'rgba(139, 92, 246, 0.05)',
  },
  betaBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.125rem 0.5rem',
    background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
    color: 'white',
    fontSize: '0.625rem',
    fontWeight: 600,
    borderRadius: '9999px',
    marginLeft: '0.5rem',
  },
  personItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '0.75rem',
    borderBottom: '1px solid var(--line)',
    cursor: 'pointer',
  },
  personInfo: {
    flex: 1,
    minWidth: 0,
  },
  personName: {
    fontWeight: 500,
    fontSize: '0.875rem',
  },
  personRole: {
    fontSize: '0.75rem',
    color: 'var(--text-muted)',
    marginTop: '0.125rem',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    accentColor: 'var(--brand)',
    cursor: 'pointer',
    marginTop: '2px',
  },
  budgetOptions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '0.5rem',
  },
  budgetOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.625rem',
    border: '1px solid var(--line)',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
  },
  addLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    color: 'var(--brand)',
    fontSize: '0.75rem',
    fontWeight: 500,
    cursor: 'pointer',
    marginTop: '0.25rem',
    background: 'none',
    border: 'none',
    padding: 0,
  },
};

// =============================================================================
// COMPONENT
// =============================================================================

export function ProjectWidget({ projectId, onClose, onSave }: ProjectFormProps) {
  const { t } = useTranslation();
  const { getProject } = useProjectsStore();
  const { data: companies = [] } = useCompanies();
  const createCompany = useCreateCompany();
  const { data: teamMembers = [] } = useTeamMembers();
  const { user } = useAuth();
  const existingProject = projectId ? getProject(projectId) : null;

  // People selectable for the project: active team members, normalized to a
  // single shape. When the team list is empty (the backend exposes no team
  // query yet), the logged-in user is the only option, so the required owner
  // select never blocks project creation.
  const projectPeople = useMemo(() => {
    const active = teamMembers
      .filter(m => m.status === 'active')
      .map(m => ({ id: m.id, name: m.displayName || m.email, role: m.role as string }));
    if (active.length > 0 || !user) return active;
    return [{ id: user.id, name: user.displayName || user.email, role: 'owner' }];
  }, [teamMembers, user]);


  // Default team: all active team members
  const defaultTeam = useMemo(() => {
    return projectPeople.map(p => p.id);
  }, [projectPeople]);

  // Form state
  const [creationMethod, setCreationMethod] = useState<CreationMethod>('scratch');
  const [formData, setFormData] = useState<ProjectFormData>({
    title: existingProject?.title || '',
    description: existingProject?.description || '',
    clientName: existingProject?.clientName || '',
    clientId: existingProject?.clientId,
    status: existingProject?.status || 'da-iniziare',
    startAt: existingProject?.startAt || new Date().toISOString().split('T')[0],
    dueAt: existingProject?.dueAt || '',
    budget: (existingProject as any)?.budget || undefined,
    budgetType: 'no-budget',
    contractId: (existingProject as any)?.contractId || undefined,
    workflow: undefined,
    team: existingProject?.team || defaultTeam,
    ownerId: undefined,
    tags: existingProject?.tags || [],
    creationMethod: 'scratch',
  });

  // Team members load asynchronously: pick a default owner once candidates arrive.
  useEffect(() => {
    if (formData.ownerId || projectPeople.length === 0) return;
    const defaultOwner = projectPeople.find(p => p.role === 'owner') ?? projectPeople[0];
    setFormData(prev => ({
      ...prev,
      ownerId: defaultOwner.id,
      team: prev.team.length > 0 ? prev.team : projectPeople.map(p => p.id),
    }));
  }, [projectPeople, formData.ownerId]);

  const [showAddCompany, setShowAddCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [companySearch, setCompanySearch] = useState('');

  // Filter companies based on search
  const filteredCompanies = useMemo(() => {
    if (!companySearch) return companies;
    const search = companySearch.toLowerCase();
    return companies.filter(c => c.name.toLowerCase().includes(search));
  }, [companies, companySearch]);

  // Handlers
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ ...formData, creationMethod });
  };

  const handleAddCompany = async () => {
    if (!newCompanyName.trim() || createCompany.isPending) return;
    try {
      const company = await createCompany.mutateAsync(newCompanyName);
      setFormData(prev => ({ ...prev, clientId: company.id, clientName: company.name }));
      setShowAddCompany(false);
      setNewCompanyName('');
      setCompanySearch('');
    } catch (error) {
      console.error('Create company failed', error);
    }
  };

  const toggleTeamMember = (memberId: string) => {
    if (formData.team.includes(memberId)) {
      setFormData({ ...formData, team: formData.team.filter(id => id !== memberId) });
    } else {
      setFormData({ ...formData, team: [...formData.team, memberId] });
    }
  };

  const selectCompany = (company: Company) => {
    setFormData({
      ...formData,
      clientId: company.id,
      clientName: company.name,
    });
    setCompanySearch('');
  };

  return (
    <DetailPanel
      isOpen={true}
      onClose={onClose}
      title={projectId ? t('projectForm.editProject') : t('projectForm.newProject')}
      icon={<Briefcase size={20} />}
      headerColor="#8b5cf6"
      width="50%"
    >
      <form onSubmit={handleSubmit} style={{ padding: '0 1rem 1rem' }}>

        {/* ================================================================= */}
        {/* STEP 1: CREATION METHOD */}
        {/* ================================================================= */}
        {!projectId && (
          <>
            <div style={styles.sectionTitle}>{t('projectForm.howToCreate')}</div>
            <div style={styles.radioGroup}>
              {/* Da zero */}
              <label
                style={{
                  ...styles.radioOption,
                  ...(creationMethod === 'scratch' ? styles.radioOptionSelected : {}),
                }}
                onClick={() => setCreationMethod('scratch')}
              >
                <input
                  type="radio"
                  name="creationMethod"
                  value="scratch"
                  checked={creationMethod === 'scratch'}
                  onChange={() => setCreationMethod('scratch')}
                  style={{ accentColor: 'var(--brand)' }}
                />
                <FileText size={20} style={{ color: creationMethod === 'scratch' ? 'var(--brand)' : '#666' }} />
                <span style={{ fontWeight: creationMethod === 'scratch' ? 600 : 400 }}>{t('projectForm.startFromScratch')}</span>
              </label>

              {/* Template */}
              <label
                style={{
                  ...styles.radioOption,
                  ...(creationMethod === 'template' ? styles.radioOptionSelected : {}),
                }}
                onClick={() => setCreationMethod('template')}
              >
                <input
                  type="radio"
                  name="creationMethod"
                  value="template"
                  checked={creationMethod === 'template'}
                  onChange={() => setCreationMethod('template')}
                  style={{ accentColor: 'var(--brand)' }}
                />
                <Layers size={20} style={{ color: creationMethod === 'template' ? 'var(--brand)' : '#666' }} />
                <span style={{ fontWeight: creationMethod === 'template' ? 600 : 400 }}>{t('projectForm.useTemplate')}</span>
              </label>

              {/* AI Wizard */}
              <label
                style={{
                  ...styles.radioOption,
                  ...(creationMethod === 'ai-wizard' ? styles.radioOptionSelected : {}),
                }}
                onClick={() => setCreationMethod('ai-wizard')}
              >
                <input
                  type="radio"
                  name="creationMethod"
                  value="ai-wizard"
                  checked={creationMethod === 'ai-wizard'}
                  onChange={() => setCreationMethod('ai-wizard')}
                  style={{ accentColor: 'var(--brand)' }}
                />
                <Sparkles size={20} style={{ color: creationMethod === 'ai-wizard' ? 'var(--brand)' : '#666' }} />
                <span style={{ fontWeight: creationMethod === 'ai-wizard' ? 600 : 400 }}>
                  {t('projectForm.aiProjectWizard')}
                  <span style={styles.betaBadge}>Beta</span>
                </span>
              </label>
            </div>
          </>
        )}

        {/* ================================================================= */}
        {/* STEP 2: PROJECT DETAILS */}
        {/* ================================================================= */}
        <div style={styles.sectionTitle}>{t('projectForm.projectDetails')}</div>

        {/* Nome progetto */}
        <div style={styles.formGroup}>
          <label style={styles.label}>{t('projectForm.projectNameRequired')}</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder={t('projectForm.projectNamePlaceholder')}
            required
            autoFocus
            style={styles.input}
          />
          <div style={styles.helpText}>{t('projectForm.projectNameUnique')}</div>
        </div>

        {/* Azienda */}
        <div style={styles.formGroup}>
          <label style={styles.label}>{t('projectForm.companyRequired')}</label>
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '0.625rem', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
              <input
                type="text"
                value={formData.clientName || companySearch}
                onChange={(e) => {
                  setCompanySearch(e.target.value);
                  if (formData.clientId) {
                    setFormData({ ...formData, clientId: undefined, clientName: '' });
                  }
                }}
                placeholder={t('projectForm.searchOrSelectCompany')}
                style={{ ...styles.input, paddingLeft: '2.25rem' }}
              />
              <ChevronDown size={16} style={{ position: 'absolute', right: '0.625rem', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
            </div>

            {/* Dropdown */}
            {companySearch && !formData.clientId && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                background: 'white', border: '1px solid var(--line)', borderRadius: '0.375rem',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '200px', overflowY: 'auto',
                marginTop: '0.25rem',
              }}>
                {filteredCompanies.map(company => (
                  <button
                    key={company.id}
                    type="button"
                    onClick={() => selectCompany(company)}
                    style={{
                      width: '100%', padding: '0.625rem', textAlign: 'left',
                      border: 'none', background: 'transparent', cursor: 'pointer',
                      borderBottom: '1px solid var(--line)',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{company.name}</div>
                  </button>
                ))}

                {/* Add new company */}
                <button
                  type="button"
                  onClick={() => {
                    setNewCompanyName(companySearch);
                    setShowAddCompany(true);
                  }}
                  style={{
                    width: '100%', padding: '0.75rem', textAlign: 'left',
                    border: 'none', background: '#f0fdf4', cursor: 'pointer',
                    color: '#16a34a', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.5rem',
                  }}
                >
                  <Plus size={16} />
                  {t('projectForm.addCompany', { name: companySearch })}
                </button>
              </div>
            )}
          </div>

          {/* Add company form */}
          {showAddCompany && (
            <div style={{
              marginTop: '0.5rem', padding: '0.75rem', background: '#f0fdf4',
              borderRadius: '0.375rem', border: '1px solid #bbf7d0',
            }}>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={newCompanyName}
                  onChange={(e) => setNewCompanyName(e.target.value)}
                  placeholder={t('projectForm.companyNamePlaceholder')}
                  style={{ ...styles.input, flex: 1 }}
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCompany())}
                />
                <button
                  type="button"
                  onClick={handleAddCompany}
                  disabled={createCompany.isPending}
                  aria-label={t('projectForm.addCompany', { name: newCompanyName })}
                  style={{ padding: '0.625rem', background: '#16a34a', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer' }}
                >
                  <Check size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddCompany(false)}
                  style={{ padding: '0.625rem', background: 'white', border: '1px solid var(--line)', borderRadius: '0.375rem', cursor: 'pointer' }}
                >
                  <X size={16} />
                </button>
              </div>
              {createCompany.isError && (
                <div role="alert" style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--color-danger)' }}>
                  {t('projectForm.createCompanyError')}
                </div>
              )}
            </div>
          )}

          <button type="button" style={styles.addLink} onClick={() => setShowAddCompany(true)}>
            <Plus size={12} /> {t('projectForm.addNewCompany')}
          </button>
        </div>

        {/* Workflow */}
        <div style={styles.formGroup}>
          <label style={styles.label}>{t('projectForm.workflow')}</label>
          <select
            value={formData.workflow || ''}
            onChange={(e) => setFormData({ ...formData, workflow: e.target.value || undefined })}
            style={styles.input}
          >
            <option value="">{t('projectForm.noWorkflow')}</option>
            <option value="standard">{t('projectForm.standardWorkflow')}</option>
            <option value="agile">{t('projectForm.agileWorkflow')}</option>
            <option value="waterfall">{t('projectForm.waterfallWorkflow')}</option>
            <option value="custom">{t('projectForm.customWorkflow')}</option>
          </select>
        </div>

        {/* Descrizione */}
        <div style={styles.formGroup}>
          <label style={styles.label}>{t('projectForm.projectDescription')}</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder={t('projectForm.descriptionPlaceholder')}
            rows={3}
            style={{ ...styles.input, resize: 'vertical' }}
          />
        </div>

        {/* ================================================================= */}
        {/* BUDGET */}
        {/* ================================================================= */}
        <div style={styles.sectionTitle}>{t('projectForm.budget')}</div>

        <div style={styles.formGroup}>
          <label style={styles.label}>{t('projectForm.selectBudgetType')}</label>
          <div style={styles.budgetOptions}>
            {[
              { value: 'no-budget', labelKey: 'projectForm.noBudget' },
              { value: 'fixed-fee', labelKey: 'projectForm.fixedFee' },
              { value: 'hourly', labelKey: 'projectForm.hourly' },
              { value: 'retainer', labelKey: 'projectForm.retainer' },
            ].map(option => (
              <label
                key={option.value}
                style={{
                  ...styles.budgetOption,
                  ...(formData.budgetType === option.value ? { border: '2px solid var(--brand)', background: 'rgba(139, 92, 246, 0.05)' } : {}),
                }}
              >
                <input
                  type="radio"
                  name="budgetType"
                  value={option.value}
                  checked={formData.budgetType === option.value}
                  onChange={() => setFormData({ ...formData, budgetType: option.value as BudgetType })}
                  style={{ accentColor: 'var(--brand)' }}
                />
                {t(option.labelKey)}
              </label>
            ))}
          </div>
        </div>

        {/* Budget amount (if not no-budget) */}
        {formData.budgetType !== 'no-budget' && (
          <div style={styles.formGroup}>
            <label style={styles.label}>
              {formData.budgetType === 'hourly' ? t('projectForm.hourlyRate') : t('projectForm.budgetAmount')}
            </label>
            <input
              type="number"
              value={formData.budget || ''}
              onChange={(e) => setFormData({ ...formData, budget: e.target.value ? parseFloat(e.target.value) : undefined })}
              placeholder={formData.budgetType === 'hourly' ? t('projectForm.hourlyPlaceholder') : t('projectForm.budgetPlaceholder')}
              style={styles.input}
              min="0"
              step={formData.budgetType === 'hourly' ? '5' : '100'}
            />
          </div>
        )}

        {/* ================================================================= */}
        {/* TEAM */}
        {/* ================================================================= */}
        <div style={styles.sectionTitle}>{t('projectForm.team')}</div>

        <div style={styles.formGroup}>
          <label style={styles.label}>{t('projectForm.addPeopleToProject')}</label>
          <div style={{ border: '1px solid var(--line)', borderRadius: '0.375rem', maxHeight: '300px', overflowY: 'auto' }}>
            {projectPeople.map(member => {
              const isSelected = formData.team.includes(member.id);
              const isOwner = formData.ownerId === member.id;
              return (
                <div
                  key={member.id}
                  style={{
                    ...styles.personItem,
                    background: isSelected ? 'rgba(139, 92, 246, 0.05)' : 'transparent',
                  }}
                  onClick={() => toggleTeamMember(member.id)}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleTeamMember(member.id)}
                    style={styles.checkbox}
                  />
                  <div style={styles.personInfo}>
                    <div style={styles.personName}>
                      {member.name}
                      {isOwner && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> {t('projectForm.me')}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Project Owner */}
        <div style={styles.formGroup}>
          <label style={styles.label}>{t('projectForm.selectProjectOwner')}</label>
          <select
            value={formData.ownerId || ''}
            onChange={(e) => setFormData({ ...formData, ownerId: e.target.value })}
            required
            style={styles.input}
          >
            <option value="">{t('projectForm.choose')}</option>
            {projectPeople.map(member => (
              <option key={member.id} value={member.id}>
                {member.name}
              </option>
            ))}
          </select>
        </div>

        {/* ================================================================= */}
        {/* DATE */}
        {/* ================================================================= */}
        <div style={styles.sectionTitle}>{t('projectForm.dates')}</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t('projectForm.startDate')}</label>
            <input
              type="date"
              value={formData.startAt}
              onChange={(e) => setFormData({ ...formData, startAt: e.target.value })}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t('projectForm.deadline')}</label>
            <input
              type="date"
              value={formData.dueAt}
              onChange={(e) => setFormData({ ...formData, dueAt: e.target.value })}
              style={styles.input}
            />
          </div>
        </div>

        {/* ================================================================= */}
        {/* ACTIONS */}
        {/* ================================================================= */}
        <div style={{
          padding: '1rem 0',
          marginTop: '1rem',
          borderTop: '1px solid var(--line)',
          display: 'flex', gap: '0.75rem', justifyContent: 'flex-end',
          position: 'sticky', bottom: 0, background: 'white',
        }}>
          <Button variant="secondary" type="button" onClick={onClose}>
            {t('projectForm.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!formData.title.trim() || !formData.clientId}
          >
            {projectId ? t('projectForm.saveChanges') : t('projectForm.createProject')}
          </Button>
        </div>
      </form>
    </DetailPanel>
  );
}
